/*
 * Tmedals V034 - self-contained product image viewer (no CDN dependencies).
 * Enhances only main product pages. Dawn remains the fallback for malformed
 * data, videos, 3D media, featured products and quick-add dialogs.
 */
(() => {
  'use strict';
  if (window.__tmProductLightboxV034) return;
  window.__tmProductLightboxV034 = true;
  const OWNER = 'product-info.tm-product-expanded:not([data-original-section])';
  const INTERVAL = 4000;
  let active = null;
  const svg = (body) => `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
  const ICONS = {
    zoom: svg('<circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 5 5M7.5 10.5h6M10.5 7.5v6"/>'),
    fit: svg('<circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 5 5M7.5 10.5h6"/>'),
    play: '<span class="tm-v056-control-label">Play</span>',
    pause: '<span class="tm-v056-control-label">Pause</span>',
    full: svg('<path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5"/>'),
    exitFull: svg('<path d="M3 8h5V3m8 0v5h5M8 21v-5H3m18 0h-5v5"/>'),
    grid: svg('<rect x="3" y="3" width="7" height="7" rx=".5"/><rect x="14" y="3" width="7" height="7" rx=".5"/><rect x="3" y="14" width="7" height="7" rx=".5"/><rect x="14" y="14" width="7" height="7" rx=".5"/>'),
    close: svg('<path d="m6 6 12 12M18 6 6 18"/>'),
    prev: '<span class="tm-v056-control-label">Previous</span>',
    next: '<span class="tm-v056-control-label">Next</span>'
  };
  const button = (action, label, icon, extra = '') => `<button type="button" data-tm-action="${action}" class="${extra}" title="${label}" aria-label="${label}">${ICONS[icon]}</button>`;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  class ProductLightbox {
    constructor(data, index, opener, owner) {
      this.items = data.images;
      this.title = String(data.title || 'Product images');
      this.opener = opener;
      this.owner = owner;
      this.index = index;
      this.cleanups = [];
      this.pointers = new Map();
      this.playing = false;
      this.closed = false;
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      this.fullscreenPending = false;
      this.root = document.createElement('div');
      this.root.className = 'tm-lightbox';
      this.root.id = 'TMProductLightbox';
      this.root.setAttribute('role', 'dialog');
      this.root.setAttribute('aria-modal', 'true');
      this.root.setAttribute('aria-labelledby', 'TMProductLightboxTitle');
      this.root.tabIndex = -1;
      this.root.innerHTML = `
        <span id="TMProductLightboxTitle" class="tm-lb__sr"></span>
        <div class="tm-lb__bar">
          <span class="tm-lb__count" aria-live="polite" aria-atomic="true"></span>
          <div class="tm-lb__tools" role="group" aria-label="Image controls">
            ${button('zoom', 'Zoom in', 'zoom')}
            ${button('play', 'Start slideshow', 'play')}
            ${button('fullscreen', 'Enter full screen', 'full')}
            ${button('thumbs', 'Hide thumbnails', 'grid')}
            ${button('close', 'Close gallery (Esc)', 'close')}
          </div>
        </div>
        <div class="tm-lb__stage">
          <img class="tm-lb__image" alt="" draggable="false" decoding="async">
          <span class="tm-lb__spinner" role="status" aria-label="Loading image"></span>
          <div class="tm-lb__error" hidden>Unable to load this image.<br><button class="tm-lb__retry" type="button" data-tm-action="retry">Try again</button></div>
        </div>
        <div class="tm-lb__navigation" role="group" aria-label="Browse images">
          ${button('prev', 'Previous image', 'prev', 'tm-lb__arrow tm-lb__prev')}
          ${button('next', 'Next image', 'next', 'tm-lb__arrow tm-lb__next')}
        </div>
        <div class="tm-lb__footer">
          <span class="tm-lb__caption"></span>
          <div id="TMProductLightboxThumbs" class="tm-lb__thumbs" role="group" aria-label="All product images"></div>
        </div>
        <span class="tm-lb__sr tm-lb__status" role="status" aria-live="polite"></span>`;
      this.stage = this.root.querySelector('.tm-lb__stage');
      this.image = this.root.querySelector('.tm-lb__image');
      this.thumbs = this.root.querySelector('.tm-lb__thumbs');
      this.count = this.root.querySelector('.tm-lb__count');
      this.caption = this.root.querySelector('.tm-lb__caption');
      this.status = this.root.querySelector('.tm-lb__status');
      this.spinner = this.root.querySelector('.tm-lb__spinner');
      this.error = this.root.querySelector('.tm-lb__error');
      this.root.querySelector('#TMProductLightboxTitle').textContent = `${this.title} — image gallery`;
      this.buttons = Object.fromEntries(Array.from(this.root.querySelectorAll('[data-tm-action]')).map(el => [el.dataset.tmAction, el]));
      this.buttons.play.setAttribute('aria-pressed', 'false');
      this.buttons.fullscreen.setAttribute('aria-pressed', 'false');
      this.buttons.thumbs.setAttribute('aria-expanded', 'true');
      this.buttons.thumbs.setAttribute('aria-controls', this.thumbs.id);
      this.items.forEach((item, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tm-lb__thumb';
        b.dataset.tmIndex = String(i);
        b.setAttribute('aria-label', `View image ${i + 1}: ${item.alt || this.title}`);
        const img = document.createElement('img');
        img.src = item.thumb || item.src;
        img.alt = '';
        img.width = 64;
        img.height = 64;
        img.loading = 'lazy';
        img.draggable = false;
        b.appendChild(img);
        this.thumbs.appendChild(b);
      });
      this.thumbButtons = Array.from(this.thumbs.children);
      this.buttons.prev.hidden = this.buttons.next.hidden = this.items.length < 2;
      this.root.querySelector('.tm-lb__navigation').hidden = this.items.length < 2;
      this.buttons.play.disabled = this.items.length < 2;
      this.buttons.fullscreen.hidden = !(
        (this.root.requestFullscreen && document.fullscreenEnabled !== false) ||
        (this.root.webkitRequestFullscreen && document.webkitFullscreenEnabled !== false)
      );
    }

    listen(target, name, fn, options) {
      target.addEventListener(name, fn, options);
      this.cleanups.push(() => target.removeEventListener(name, fn, options));
    }

    open() {
      this.scroll = { x: window.scrollX, y: window.scrollY };
      this.lockPage();
      document.body.appendChild(this.root);
      this.inertNodes = Array.from(document.body.children).filter(el => el !== this.root && !['SCRIPT', 'STYLE', 'LINK'].includes(el.tagName));
      this.inertStates = this.inertNodes.map(el => el.inert);
      this.inertNodes.forEach(el => { el.inert = true; });
      this.listen(this.root, 'click', event => this.onClick(event));
      this.listen(document, 'keydown', event => this.onKeyDown(event), true);
      this.listen(document, 'focusin', event => {
        if (!this.closed && !this.root.contains(event.target)) this.buttons.close.focus({ preventScroll: true });
      });
      this.listen(document, 'visibilitychange', () => { if (document.hidden) this.setPlaying(false); });
      this.listen(document, 'fullscreenchange', () => this.updateFullscreen());
      this.listen(document, 'webkitfullscreenchange', () => this.updateFullscreen());
      this.listen(window, 'resize', () => this.fit());
      this.listen(this.stage, 'wheel', event => this.onWheel(event), { passive: false });
      this.listen(this.stage, 'pointerdown', event => this.onPointerDown(event));
      this.listen(this.stage, 'pointermove', event => this.onPointerMove(event));
      this.listen(this.stage, 'pointerup', event => this.onPointerUp(event));
      this.listen(this.stage, 'pointercancel', () => this.cancelGesture());
      this.listen(this.image, 'dragstart', event => event.preventDefault());
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.fit());
        this.resizeObserver.observe(this.stage);
      }
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      this.show(this.index);
      this.buttons.close.focus({ preventScroll: true });
      return this;
    }

    lockPage() {
      const body = document.body;
      this.savedBody = {};
      const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const padding = parseFloat(getComputedStyle(body).paddingRight) || 0;
      for (const prop of ['position', 'top', 'left', 'right', 'width', 'overflow', 'padding-right']) {
        this.savedBody[prop] = [body.style.getPropertyValue(prop), body.style.getPropertyPriority(prop)];
      }
      this.savedHtmlOverflow = [document.documentElement.style.getPropertyValue('overflow'), document.documentElement.style.getPropertyPriority('overflow')];
      body.style.setProperty('position', 'fixed', 'important');
      body.style.setProperty('top', `${-this.scroll.y}px`, 'important');
      body.style.setProperty('left', `${-this.scroll.x}px`, 'important');
      body.style.setProperty('right', '0', 'important');
      body.style.setProperty('width', '100%', 'important');
      body.style.setProperty('overflow', 'hidden', 'important');
      if (scrollbar) body.style.setProperty('padding-right', `${padding + scrollbar}px`, 'important');
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    }

    unlockPage() {
      for (const [prop, [value, priority]] of Object.entries(this.savedBody)) {
        if (value) document.body.style.setProperty(prop, value, priority);
        else document.body.style.removeProperty(prop);
      }
      const html = document.documentElement;
      const [value, priority] = this.savedHtmlOverflow;
      if (value) html.style.setProperty('overflow', value, priority);
      else html.style.removeProperty('overflow');
      const previous = [html.style.getPropertyValue('scroll-behavior'), html.style.getPropertyPriority('scroll-behavior')];
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(this.scroll.x, this.scroll.y);
      if (previous[0]) html.style.setProperty('scroll-behavior', previous[0], previous[1]);
      else html.style.removeProperty('scroll-behavior');
    }

    show(index, automatic = false) {
      if (this.closed) return;
      if (!automatic) this.setPlaying(false);
      clearTimeout(this.timer);
      this.index = (index + this.items.length) % this.items.length;
      this.loaded = false;
      this.scale = 1;
      this.pan = { x: 0, y: 0 };
      this.cancelGesture();
      const item = this.items[this.index];
      const token = this.loadToken = (this.loadToken || 0) + 1;
      this.count.textContent = `${this.index + 1} / ${this.items.length}`;
      this.caption.textContent = item.alt || this.title;
      this.image.alt = item.alt || this.title;
      this.image.style.visibility = 'hidden';
      this.spinner.hidden = false;
      this.error.hidden = true;
      this.thumbButtons.forEach((b, i) => {
        if (i === this.index) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
      this.fit();
      this.image.onload = () => {
        if (this.closed || token !== this.loadToken) return;
        this.loaded = true;
        this.spinner.hidden = true;
        this.image.style.visibility = 'visible';
        this.schedule();
        this.preloadNeighbours();
      };
      this.image.onerror = () => {
        if (this.closed || token !== this.loadToken) return;
        this.loaded = true;
        this.spinner.hidden = true;
        this.error.hidden = false;
        this.status.textContent = 'Unable to load this image. Use Try again or continue to the next image.';
        this.schedule();
      };
      if (item.srcset) this.image.srcset = item.srcset;
      else this.image.removeAttribute('srcset');
      this.image.src = item.src;
      if (this.image.complete && this.image.naturalWidth) this.image.onload();
      requestAnimationFrame(() => { if (!this.closed) this.revealActiveThumb(); });
    }

    preloadNeighbours() {
      if (this.items.length < 2) return;
      this.preloads = [-1, 1].map(delta => {
        const item = this.items[(this.index + delta + this.items.length) % this.items.length];
        const image = new Image();
        image.sizes = `${Math.ceil(this.stage.clientWidth)}px`;
        if (item.srcset) image.srcset = item.srcset;
        image.src = item.src;
        return image;
      });
    }

    fit() {
      if (this.closed) return;
      const item = this.items[this.index];
      if (!item) return;
      const roomX = this.stage.clientWidth < 600 ? 16 : 128;
      const factor = Math.min(1, Math.max(1, this.stage.clientWidth - roomX) / item.width,
        Math.max(1, this.stage.clientHeight - 26) / item.height);
      this.baseW = item.width * factor;
      this.baseH = item.height * factor;
      this.maxScale = Math.max(2, Math.min(5, 1.5 / factor));
      this.scale = clamp(this.scale, 1, this.maxScale);
      this.image.style.width = `${this.baseW}px`;
      this.image.style.height = `${this.baseH}px`;
      this.transform();
    }

    transform() {
      const limitX = Math.max(0, (this.baseW * this.scale - this.stage.clientWidth) / 2);
      const limitY = Math.max(0, (this.baseH * this.scale - this.stage.clientHeight) / 2);
      this.pan.x = clamp(this.pan.x, -limitX, limitX);
      this.pan.y = clamp(this.pan.y, -limitY, limitY);
      this.image.style.transform = `translate(-50%, -50%) translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`;
      this.image.sizes = `${Math.ceil(this.baseW * this.scale)}px`;
      const zoomed = this.scale > 1.01;
      this.root.classList.toggle('is-zoomed', zoomed);
      this.buttons.zoom.innerHTML = zoomed ? ICONS.fit : ICONS.zoom;
      const label = zoomed ? 'Fit image to screen' : 'Zoom in';
      this.buttons.zoom.title = label;
      this.buttons.zoom.setAttribute('aria-label', label);
      this.buttons.zoom.setAttribute('aria-pressed', String(zoomed));
    }

    zoomTo(scale, point) {
      this.setPlaying(false);
      const next = clamp(scale, 1, this.maxScale);
      const ratio = next / this.scale;
      if (point) {
        const rect = this.stage.getBoundingClientRect();
        const x = point.x - rect.left - rect.width / 2;
        const y = point.y - rect.top - rect.height / 2;
        this.pan.x = x - (x - this.pan.x) * ratio;
        this.pan.y = y - (y - this.pan.y) * ratio;
      }
      this.scale = next;
      if (next <= 1.01) this.pan = { x: 0, y: 0 };
      this.transform();
    }

    toggleZoom(point) {
      const target = this.scale > 1.01 ? 1 : Math.min(this.maxScale, Math.max(2, this.items[this.index].width / this.baseW));
      this.zoomTo(target, point);
    }

    onWheel(event) {
      if (event.target.closest('button')) return;
      event.preventDefault();
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 200 : 1);
      this.zoomTo(this.scale * Math.exp(-delta * (event.ctrlKey ? .009 : .002)), { x: event.clientX, y: event.clientY });
    }

    onClick(event) {
      const thumb = event.target.closest('[data-tm-index]');
      if (thumb) { this.show(Number(thumb.dataset.tmIndex)); return; }
      const action = event.target.closest('[data-tm-action]')?.dataset.tmAction;
      switch (action) {
        case 'close': this.close(); break;
        case 'prev': this.show(this.index - 1); break;
        case 'next': this.show(this.index + 1); break;
        case 'zoom': this.toggleZoom(); break;
        case 'play': this.setPlaying(!this.playing); break;
        case 'fullscreen': this.toggleFullscreen(); break;
        case 'thumbs': {
          this.thumbs.hidden = !this.thumbs.hidden;
          this.buttons.thumbs.setAttribute('aria-expanded', String(!this.thumbs.hidden));
          const label = this.thumbs.hidden ? 'Show thumbnails' : 'Hide thumbnails';
          this.buttons.thumbs.title = label;
          this.buttons.thumbs.setAttribute('aria-label', label);
          this.fit();
          this.revealActiveThumb();
          break;
        }
        case 'retry': this.show(this.index); break;
      }
    }

    onKeyDown(event) {
      if (this.closed || event.ctrlKey || event.metaKey || event.altKey) return;
      const key = event.key;
      if (key === 'Tab') {
        const focusable = Array.from(this.root.querySelectorAll('button:not(:disabled),[tabindex="0"]'))
          .filter(el => el.getClientRects().length && !el.closest('[hidden]'));
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.root)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        return;
      }
      if (!['Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End', '+', '=', '-', '_', '0', ' '].includes(key)) return;
      if (key === ' ' && event.target.closest('button')) return;
      event.preventDefault();
      event.stopPropagation();
      if (key === 'Escape') this.close();
      else if (key === 'ArrowLeft') this.show(this.index - 1);
      else if (key === 'ArrowRight') this.show(this.index + 1);
      else if (key === 'Home') this.show(0);
      else if (key === 'End') this.show(this.items.length - 1);
      else if (key === '+' || key === '=') this.zoomTo(this.scale * 1.5);
      else if (key === '-' || key === '_') this.zoomTo(this.scale / 1.5);
      else if (key === '0') this.zoomTo(1);
      else if (key === ' ') this.setPlaying(!this.playing);
    }

    setPlaying(value) {
      this.playing = !!value && this.items.length > 1 && !this.closed;
      clearTimeout(this.timer);
      this.buttons.play.innerHTML = this.playing ? ICONS.pause : ICONS.play;
      const label = this.playing ? 'Pause slideshow' : 'Start slideshow';
      this.buttons.play.title = label;
      this.buttons.play.setAttribute('aria-label', label);
      this.buttons.play.setAttribute('aria-pressed', String(this.playing));
      if (this.playing) {
        this.scale = 1;
        this.pan = { x: 0, y: 0 };
        this.transform();
        this.schedule();
      }
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.playing || !this.loaded || this.closed || document.hidden) return;
      this.timer = setTimeout(() => this.show(this.index + 1, true), INTERVAL);
    }

    revealActiveThumb() {
      if (this.thumbs.hidden) return;
      const b = this.thumbButtons[this.index];
      if (!b) return;
      const br = b.getBoundingClientRect(), tr = this.thumbs.getBoundingClientRect();
      if (br.left < tr.left || br.right > tr.right) this.thumbs.scrollLeft += br.left - tr.left - (tr.width - br.width) / 2;
    }

    async toggleFullscreen() {
      if (this.fullscreenPending) return;
      this.fullscreenPending = true;
      try {
        const current = document.fullscreenElement || document.webkitFullscreenElement;
        if (current === this.root) {
          const exit = document.exitFullscreen || document.webkitExitFullscreen;
          if (exit) await exit.call(document);
        } else {
          const enter = this.root.requestFullscreen || this.root.webkitRequestFullscreen;
          if (enter) await enter.call(this.root);
        }
      } catch (_) {
        if (!this.closed) this.status.textContent = 'Full screen is not available in this browser. The image viewer remains open.';
      } finally {
        this.fullscreenPending = false;
        if (!this.closed) this.updateFullscreen();
      }
    }

    updateFullscreen() {
      const full = (document.fullscreenElement || document.webkitFullscreenElement) === this.root;
      this.buttons.fullscreen.innerHTML = full ? ICONS.exitFull : ICONS.full;
      const label = full ? 'Exit full screen' : 'Enter full screen';
      this.buttons.fullscreen.title = label;
      this.buttons.fullscreen.setAttribute('aria-label', label);
      this.buttons.fullscreen.setAttribute('aria-pressed', String(full));
      this.fit();
    }

    onPointerDown(event) {
      if (event.target.closest('button') || (event.pointerType === 'mouse' && event.button !== 0)) return;
      this.setPlaying(false);
      const point = { x: event.clientX, y: event.clientY };
      this.pointers.set(event.pointerId, point);
      try { this.stage.setPointerCapture(event.pointerId); } catch (_) { /* detached/unsupported capture */ }
      if (this.pointers.size === 1) {
        this.gesture = { start: point, last: point, moved: false, pinched: false, target: event.target };
      } else if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinch = { distance: Math.max(1, Math.hypot(b.x - a.x, b.y - a.y)), scale: this.scale,
          mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, pan: { ...this.pan } };
        this.gesture.pinched = true;
        this.gesture.moved = true;
      }
      this.root.classList.add('is-dragging');
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      const point = { x: event.clientX, y: event.clientY };
      this.pointers.set(event.pointerId, point);
      if (this.pointers.size === 2 && this.pinch) {
        const [a, b] = [...this.pointers.values()];
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const scale = clamp(this.pinch.scale * Math.hypot(b.x - a.x, b.y - a.y) / this.pinch.distance, 1, this.maxScale);
        const ratio = scale / this.pinch.scale, rect = this.stage.getBoundingClientRect();
        this.pan.x = mid.x - rect.left - rect.width / 2 - (this.pinch.mid.x - rect.left - rect.width / 2 - this.pinch.pan.x) * ratio;
        this.pan.y = mid.y - rect.top - rect.height / 2 - (this.pinch.mid.y - rect.top - rect.height / 2 - this.pinch.pan.y) * ratio;
        this.scale = scale;
        this.transform();
      } else if (this.pointers.size === 1 && this.gesture) {
        if (Math.hypot(point.x - this.gesture.start.x, point.y - this.gesture.start.y) > 6) this.gesture.moved = true;
        if (this.scale > 1.01) {
          this.pan.x += point.x - this.gesture.last.x;
          this.pan.y += point.y - this.gesture.last.y;
          this.transform();
        }
        this.gesture.last = point;
      }
    }

    onPointerUp(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      try { this.stage.releasePointerCapture(event.pointerId); } catch (_) { /* already released */ }
      const gesture = this.gesture;
      if (this.pointers.size === 1) {
        const point = [...this.pointers.values()][0];
        this.gesture = { start: point, last: point, moved: true, pinched: true, target: this.image };
        this.pinch = null;
        return;
      }
      if (this.pointers.size > 0 || !gesture) return;
      this.root.classList.remove('is-dragging');
      this.gesture = null;
      this.pinch = null;
      if (gesture.pinched) return;
      const dx = event.clientX - gesture.start.x, dy = event.clientY - gesture.start.y;
      if (this.scale <= 1.01 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.show(this.index + (dx < 0 ? 1 : -1));
      } else if (!gesture.moved && Math.hypot(dx, dy) < 7 && gesture.target === this.image) {
        const point = { x: event.clientX, y: event.clientY };
        if (event.pointerType === 'mouse') this.toggleZoom(point);
        else {
          const now = Date.now();
          if (this.lastTap && now - this.lastTap.time < 320 && Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) < 30) {
            this.toggleZoom(point); this.lastTap = null;
          } else this.lastTap = { ...point, time: now };
        }
      }
    }

    cancelGesture() {
      this.pointers.clear();
      this.gesture = null;
      this.pinch = null;
      this.root.classList.remove('is-dragging');
    }

    close() {
      if (this.closed) return;
      this.setPlaying(false);
      this.closed = true;
      clearTimeout(this.timer);
      this.loadToken = (this.loadToken || 0) + 1;
      this.image.onload = this.image.onerror = null;
      this.resizeObserver?.disconnect();
      this.cleanups.forEach(fn => fn());
      this.cleanups = [];
      const current = document.fullscreenElement || document.webkitFullscreenElement;
      if (current === this.root) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        try { Promise.resolve(exit?.call(document)).catch(() => {}); } catch (_) { /* fullscreen already exited */ }
      }
      (this.inertNodes || []).forEach((el, i) => { el.inert = this.inertStates[i]; });
      this.root.remove();
      if (this.savedBody) this.unlockPage();
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      if (active === this) active = null;
    }
  }

  // Capture the native image-button click BEFORE Dawn's single-image modal.
  // No wheel/pointer listeners are attached to the normal product page.
  document.addEventListener('click', event => {
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    const opener = target?.closest('.product__modal-opener--image');
    const owner = opener?.closest(OWNER);
    if (!owner) return;
    const trigger = opener.querySelector('button[data-media-id]');
    const dataElement = owner.querySelector('[data-tm-product-lightbox]');
    if (!trigger || !dataElement) return;
    let data;
    try {
      data = JSON.parse(dataElement.textContent);
      if (!Array.isArray(data.images)) return;
      data.images = data.images.filter(item => item && typeof item.src === 'string' && item.width > 0 && item.height > 0);
    } catch (_) { return; }
    const index = data.images.findIndex(item => String(item.id) === String(trigger.dataset.mediaId));
    if (index < 0) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (active) active.close();
    active = new ProductLightbox(data, index, trigger, owner);
    try { active.open(); }
    catch (error) {
      // Restore page scrolling and open the original modal if enhancement fails.
      try { active.close(); } catch (_) { active?.root?.remove(); active = null; }
      console.warn('Tmedals photo viewer fallback:', error);
      document.querySelector(opener.dataset.modal)?.show?.(trigger);
    }
  }, true);

  document.addEventListener('shopify:section:unload', event => {
    if (active && (event.target === active.owner || event.target.contains(active.owner))) active.close();
  });
  window.addEventListener('pagehide', () => { if (active) active.close(); });
})();
